// Time-zone helpers for the Map of the Week schedule.
//
// The schedule is authored the way a human thinks about it ("Sunday 21:00,
// Berlin time"), but a submission window has to be an absolute instant so every
// player everywhere sees the same deadline — and so the window survives DST.
// There's no date library in this project, so the zone offset is derived from
// Intl, which every supported browser ships.

export const DEFAULT_TIME_ZONE = 'Europe/Berlin';

export const WEEKDAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const zoneParts = (utcMs, timeZone) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = {};
  formatter.formatToParts(new Date(utcMs)).forEach((part) => {
    parts[part.type] = part.value;
  });
  return parts;
};

// How far the zone is ahead of UTC at this instant, in ms.
const zoneOffsetMs = (utcMs, timeZone) => {
  const p = zoneParts(utcMs, timeZone);
  // `hour` comes back as 24 at midnight under hour12:false in some engines.
  const asIfUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
  return asIfUtc - utcMs;
};

/**
 * Turn a wall-clock date + time in a zone into an absolute instant.
 * @param {string} dateKey - YYYY-MM-DD
 * @param {string} timeHHmm - HH:MM, 24h
 * @param {string} timeZone - IANA zone
 * @returns {number} epoch ms
 */
export const zonedTimeToUtc = (dateKey, timeHHmm, timeZone) => {
  const naive = Date.parse(`${dateKey}T${timeHHmm}:00Z`);
  if (Number.isNaN(naive)) return NaN;
  // Two passes: the first offset is read at the wrong instant, which only
  // matters within an hour of a DST switch. Re-reading at the corrected
  // instant settles it.
  const firstPass = naive - zoneOffsetMs(naive, timeZone);
  return naive - zoneOffsetMs(firstPass, timeZone);
};

/** Calendar date (YYYY-MM-DD) that an instant falls on, in a given zone. */
export const dateKeyInZone = (utcMs, timeZone) => {
  const p = zoneParts(utcMs, timeZone);
  return `${p.year}-${p.month}-${p.day}`;
};

/** Day of week (0 = Sunday) that an instant falls on, in a given zone. */
export const weekdayInZone = (utcMs, timeZone) => {
  const key = dateKeyInZone(utcMs, timeZone);
  return new Date(`${key}T12:00:00Z`).getUTCDay();
};

/** Shift a YYYY-MM-DD key by whole days. Zone-free: it's pure calendar maths. */
export const addDays = (dateKey, days) => {
  const ms = Date.parse(`${dateKey}T12:00:00Z`) + days * 86400000;
  return new Date(ms).toISOString().split('T')[0];
};

/**
 * The most recent occurrence of `dayOfWeek` on or before today, as a date key
 * in the given zone. This is the event week players are looking at: results
 * stay up all week until the next event day comes round.
 */
export const currentEventDate = (dayOfWeek, timeZone, now = Date.now()) => {
  const today = dateKeyInZone(now, timeZone);
  const todayDow = weekdayInZone(now, timeZone);
  const back = (todayDow - dayOfWeek + 7) % 7;
  return addDays(today, -back);
};

/** The next occurrence of `dayOfWeek` strictly after the given event date. */
export const nextEventDate = (eventDate) => addDays(eventDate, 7);

/** "Sun 21:00" style label for an instant, in the schedule's zone. */
export const formatInZone = (utcMs, timeZone, options = {}) =>
  new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...options,
  }).format(new Date(utcMs));

/** HH:MM in the viewer's own zone, for "this is when it starts for you". */
export const formatLocalTime = (utcMs) =>
  new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(utcMs));

/** "1h 20m" / "45m" / "30s" — coarse countdown, good enough at a glance. */
export const formatDuration = (ms) => {
  if (ms <= 0) return '0s';
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (totalMinutes > 0) return `${totalMinutes}m`;
  return `${Math.floor(ms / 1000)}s`;
};
